import React, { useState } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollLock';

/**
 * Hook zarządzający narzędziami rysunkowymi.
 * Posiada stany trybu rysowania i handlerów.
 * Dane rysunków (drawings/setDrawings) pochodzą z useProjectData.
 */
export function useDrawings({ drawings, setDrawings, getSvgCoordinates, scale, horizontalScale, initialData }) {
  const initDrawingSettings = initialData?.drawingSettings || {};
  const [drawingMode, setDrawingMode] = useState(null);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [drawingColor, setDrawingColor] = useState(initDrawingSettings.drawingColor || '#FF0000');
  const [scaleDrawingTools, setScaleDrawingTools] = useState(initDrawingSettings.scaleDrawingTools ?? false);
  const [moveDrawingMode, setMoveDrawingMode] = useState(false);
  const [draggingDrawing, setDraggingDrawing] = useState(null);
  const [measureFirstPoint, setMeasureFirstPoint] = useState(null);
  const [measurePreview, setMeasurePreview] = useState(null);
  const [activeMeasurements, setActiveMeasurements] = useState(initialData?.activeMeasurements || []);

  /**
   * @param {Event} e
   * @param {Object} guards - opcjonalne flagi blokujące rysowanie
   */
  const handleDrawingStart = (e, guards = {}) => {
    if (!drawingMode || guards.interpolationMode || guards.resizingLayer || guards.draggingBorehole) return;

    const { x, y } = getSvgCoordinates(e);

    if (drawingMode === 'measure') {
      if (!measureFirstPoint) {
        setMeasureFirstPoint({ x, y });
      } else {
        setActiveMeasurements(prev => [...prev, {
          type: 'measure',
          startX: measureFirstPoint.x,
          startY: measureFirstPoint.y,
          endX: x,
          endY: y,
          color: drawingColor,
          id: Date.now(),
          originalScale: scale,
          originalHorizontalScale: horizontalScale
        }]);
        setMeasureFirstPoint(null);
        setMeasurePreview(null);
      }
      return;
    }

    setCurrentDrawing({
      type: drawingMode,
      startX: x,
      startY: y,
      endX: x,
      endY: y,
      color: drawingColor,
      id: Date.now(),
      originalScale: scale,
      originalHorizontalScale: horizontalScale
    });

    lockScroll();
  };

  const handleDrawingMove = (e) => {
    const { x, y } = getSvgCoordinates(e);

    if (drawingMode === 'measure' && measureFirstPoint) {
      setMeasurePreview({
        startX: measureFirstPoint.x,
        startY: measureFirstPoint.y,
        endX: x,
        endY: y
      });
      return;
    }

    if (!currentDrawing) return;
    e.preventDefault();

    setCurrentDrawing(prev => ({
      ...prev,
      endX: x,
      endY: y
    }));
  };

  const handleDrawingEnd = () => {
    if (currentDrawing) {
      if (currentDrawing.type === 'text') {
        const userText = prompt('Wpisz tekst do wyświetlenia:', 'Tekst');
        if (userText && userText.trim() !== '') {
          setDrawings(prev => [...prev, {
            ...currentDrawing,
            text: userText.trim()
          }]);
        }
      } else {
        const hasSize = Math.abs(currentDrawing.endX - currentDrawing.startX) > 5 ||
                        Math.abs(currentDrawing.endY - currentDrawing.startY) > 5;
        if (hasSize) {
          setDrawings(prev => [...prev, currentDrawing]);
        }
      }
      setCurrentDrawing(null);
    }
    unlockScroll();
  };

  const removeDrawing = (drawingId) => {
    setDrawings(prev => prev.filter(d => d.id !== drawingId));
  };

  const clearAllDrawings = () => {
    if (window.confirm('Czy na pewno chcesz usunąć wszystkie rysunki?')) {
      setDrawings([]);
    }
  };

  const handleDrawingDragStart = (e, drawingId) => {
    if (!moveDrawingMode) return;
    e.stopPropagation();

    const { x, y } = getSvgCoordinates(e);
    const drawing = drawings.find(d => d.id === drawingId);
    if (!drawing) return;

    setDraggingDrawing({
      id: drawingId,
      offsetX: x - (drawing.startX || 0),
      offsetY: y - (drawing.startY || 0)
    });
  };

  const handleDrawingDragMove = (e) => {
    if (!draggingDrawing) return;
    e.preventDefault();

    const { x, y } = getSvgCoordinates(e);
    const drawing = drawings.find(d => d.id === draggingDrawing.id);
    if (!drawing) return;

    const newX = x - draggingDrawing.offsetX;
    const newY = y - draggingDrawing.offsetY;

    setDrawings(prev => prev.map(d => {
      if (d.id === draggingDrawing.id) {
        if (d.type === 'pencil') {
          const deltaX = newX - d.startX;
          const deltaY = newY - d.startY;
          return {
            ...d,
            points: d.points.map(p => ({ x: p.x + deltaX, y: p.y + deltaY })),
            startX: newX,
            startY: newY
          };
        } else if (d.type === 'text') {
          return { ...d, startX: newX, startY: newY };
        } else {
          const deltaX = newX - d.startX;
          const deltaY = newY - d.startY;
          return {
            ...d,
            startX: newX,
            startY: newY,
            endX: d.endX + deltaX,
            endY: d.endY + deltaY
          };
        }
      }
      return d;
    }));
  };

  const handleDrawingDragEnd = () => {
    setDraggingDrawing(null);
  };

  const renderDrawing = (drawing) => {
    const { type, startX, startY, endX, endY, color, points, originalScale, originalHorizontalScale } = drawing;

    let adjustedStartX = startX;
    let adjustedStartY = startY;
    let adjustedEndX = endX;
    let adjustedEndY = endY;
    let adjustedPoints = points;

    if (scaleDrawingTools && originalScale && originalHorizontalScale) {
      const verticalScaleFactor = scale / originalScale;
      const horizontalScaleFactor = horizontalScale / originalHorizontalScale;

      adjustedStartX = startX * horizontalScaleFactor;
      adjustedStartY = startY * verticalScaleFactor;
      adjustedEndX = endX * horizontalScaleFactor;
      adjustedEndY = endY * verticalScaleFactor;

      if (points) {
        adjustedPoints = points.map(p => ({
          x: p.x * horizontalScaleFactor,
          y: p.y * verticalScaleFactor
        }));
      }
    }

    const visualScaleFactor = scaleDrawingTools && originalScale ? (scale / originalScale) : 1;
    const strokeWidth = 2 * visualScaleFactor;
    const fontSize = 18 * visualScaleFactor;
    const measureFontSize = 14 * visualScaleFactor;
    const circleRadius = 4 * visualScaleFactor;

    switch (type) {
      case 'line':
        return (
          <line x1={adjustedStartX} y1={adjustedStartY} x2={adjustedEndX} y2={adjustedEndY}
            stroke={color} strokeWidth={strokeWidth} />
        );

      case 'arrow': {
        const angle = Math.atan2(adjustedEndY - adjustedStartY, adjustedEndX - adjustedStartX);
        const arrowLength = 15 * visualScaleFactor;
        const tip1X = adjustedEndX - arrowLength * Math.cos(angle - Math.PI / 6);
        const tip1Y = adjustedEndY - arrowLength * Math.sin(angle - Math.PI / 6);
        const tip2X = adjustedEndX - arrowLength * Math.cos(angle + Math.PI / 6);
        const tip2Y = adjustedEndY - arrowLength * Math.sin(angle + Math.PI / 6);
        return (
          <g>
            <line x1={adjustedStartX} y1={adjustedStartY} x2={adjustedEndX} y2={adjustedEndY}
              stroke={color} strokeWidth={strokeWidth} />
            <polygon points={`${adjustedEndX},${adjustedEndY} ${tip1X},${tip1Y} ${tip2X},${tip2Y}`} fill={color} />
          </g>
        );
      }

      case 'rect':
        return (
          <rect x={Math.min(adjustedStartX, adjustedEndX)} y={Math.min(adjustedStartY, adjustedEndY)}
            width={Math.abs(adjustedEndX - adjustedStartX)} height={Math.abs(adjustedEndY - adjustedStartY)}
            fill="none" stroke={color} strokeWidth={strokeWidth} />
        );

      case 'circle': {
        const radius = Math.sqrt(Math.pow(adjustedEndX - adjustedStartX, 2) + Math.pow(adjustedEndY - adjustedStartY, 2));
        return (
          <circle cx={adjustedStartX} cy={adjustedStartY} r={radius}
            fill="none" stroke={color} strokeWidth={strokeWidth} />
        );
      }

      case 'text':
        return (
          <text x={adjustedStartX} y={adjustedStartY} fill={color} fontSize={fontSize} fontWeight="bold">
            {drawing.text || 'Tekst'}
          </text>
        );

      case 'measure': {
        const midX = (adjustedStartX + adjustedEndX) / 2;
        const midY = (adjustedStartY + adjustedEndY) / 2;
        const originalDeltaX = Math.abs(endX - startX);
        const originalDeltaY = Math.abs(endY - startY);
        const originalScaleToUse = originalHorizontalScale || horizontalScale;
        const originalVerticalScaleToUse = originalScale || scale;
        const distanceXMeters = originalDeltaX / originalScaleToUse;
        const distanceYMeters = originalDeltaY / (originalVerticalScaleToUse / 10);
        const distanceMeters = Math.sqrt(Math.pow(distanceXMeters, 2) + Math.pow(distanceYMeters, 2));
        return (
          <g>
            <line x1={adjustedStartX} y1={adjustedStartY} x2={adjustedEndX} y2={adjustedEndY}
              stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${5 * visualScaleFactor},${5 * visualScaleFactor}`} />
            <circle cx={adjustedStartX} cy={adjustedStartY} r={circleRadius} fill={color} />
            <circle cx={adjustedEndX} cy={adjustedEndY} r={circleRadius} fill={color} />
            <text x={midX} y={midY - 5 * visualScaleFactor} fill={color} fontSize={measureFontSize}
              fontWeight="bold" textAnchor="middle">
              {distanceMeters.toFixed(2)}m
            </text>
          </g>
        );
      }

      case 'pencil':
        if (!adjustedPoints || adjustedPoints.length < 2) return null;
        const pathData = adjustedPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
        return (
          <path d={pathData} stroke={color} strokeWidth={strokeWidth}
            fill="none" strokeLinecap="round" strokeLinejoin="round" />
        );

      default:
        return null;
    }
  };

  return {
    // Stany
    drawingMode, setDrawingMode,
    currentDrawing, setCurrentDrawing,
    drawingColor, setDrawingColor,
    scaleDrawingTools, setScaleDrawingTools,
    moveDrawingMode, setMoveDrawingMode,
    draggingDrawing, setDraggingDrawing,
    measureFirstPoint, setMeasureFirstPoint,
    measurePreview, setMeasurePreview,
    activeMeasurements, setActiveMeasurements,
    // Handlery
    handleDrawingStart,
    handleDrawingMove,
    handleDrawingEnd,
    handleDrawingDragStart,
    handleDrawingDragMove,
    handleDrawingDragEnd,
    removeDrawing,
    clearAllDrawings,
    renderDrawing,
  };
}
